<script setup>
import { ref, onMounted, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import { usePageLoadingStore } from "../../../../stores/pageLoading";
import curriculumService from "../../services/curriculum/curriculumService";
import SNBAdmin from "../../../../layouts/nav/SNBAdmin.vue";
import { useAlertStore } from "../../../../stores/alertStore";
import SiteCurriculumDetailModal from "../../components/modal/site/SiteCurriculumDetailModal.vue";
const { $alert, $confirm } = useAlertStore();
const route = useRoute();
const router = useRouter();
const loadingStore = usePageLoadingStore();
const { $toast } = getCurrentInstance().appContext.config.globalProperties;

const currentURL = window.location.host;
const yearsArr = ref([1,2,3,4,5,6,7,8,9,10,11,12]);

onMounted(() => {
    const { id } = route.params;
    if (!id) {
        router.push({ name: "SiteCurriculumManage" });
    } else {
        retrieveCurriculumSummaryDetail(id);
    }
});

const summaryDetail = ref({
    summaryName: '',
    summaryOpenYn: '',
});
const curriculumList = ref([]);
const curriculumModal = ref(null);

const deleteYn = ref(true);

const retrieveCurriculumSummaryDetail = (summaryId) => {
    loadingStore.loading = true;
    curriculumService.retrieveCurriculumSummaryDetail(summaryId)
        .then( async (res) => {
            loadingStore.loading = false;
            if (res.data.code.toLowerCase() === "success") {
                const result = res.data.result;
                summaryDetail.value = result.summary;
                curriculumList.value = (result.curriculumList || []).slice().sort((a, b) => {
                    const ao = Number(a.curriculumOrder) || 0;
                    const bo = Number(b.curriculumOrder) || 0;
                    if (ao !== bo) return ao - bo;
                    const an = (a.curriculumName || '').toString();
                    const bn = (b.curriculumName || '').toString();
                    return an.localeCompare(bn);
                });
            }
        }).catch((res) => {
            loadingStore.loading = false;
            $toast("error", res);
    });
};

// 총괄표 복사. 현재 보고있는 총괄표정보를 복사한 총괄표 수정 페이지로 이동
const copySummary = async () => {
    // todo. 복사 api 호출
    const result = await curriculumService.copyCurriculum(summaryDetail.value.summaryId);
    if(result.data.code.toLowerCase() === "success" && result.data.result) {
        editSummary(result.data.result);
    } else {
        $alert("복사에 실패했습니다.");
    }
};

// 총괄표 수정
const editSummary = (id) => {
    router.push({ name: "SiteCurriculumUpdate", params: { id: id || summaryDetail.value.summaryId } });
};

// 총괄표 삭제
const deleteSummary = async () => {
    const ok = await $confirm(["총괄표를 삭제할까요?", "공개된 총괄표명일 경우 삭제 시 사용자 사이트에", "즉시 적용되며, 되돌릴 수 없습니다."], "삭제", "취소");
    if(ok) {
        loadingStore.loading = true;
        curriculumService.deleteCurriculum(summaryDetail.value.summaryId)
            .then(async (res) => {
                loadingStore.loading = false;
                if (res.data.code.toLowerCase() === "success") {
                    const ok = await $alert(["삭제되었습니다."]);
                    if (ok) {
                        goCurriculumList();
                    }
                }
            }).catch((res) => {
                loadingStore.loading = false;
                $toast("error", res);
            });
    }
};

// 목록으로 이동
const goCurriculumList = () => {
    router.push({ name: "SiteCurriculumManage" });
};

const handleClickRow = async (item) => {
    const result = await curriculumService.retrieveCurriculumDetail(item.curriculumId);
    curriculumModal.value.init(result.data.result);
    curriculumModal.value.show();
};
</script>

<template>
    <div class="content">
        <div class="sub-title">
            <h3>{{ summaryDetail.summaryName }}({{ summaryDetail.summaryOpenYn === 'Y' ? '공개' : '비공개' }})</h3>
        </div>
        <div class="list-top space-between">
            <div>
                <KSASecondaryButton class="gray_button ml-xs" text="목록" @click="goCurriculumList" />
                <KSASecondaryButton class="gray_button ml-xs" text="총괄표 이미지 다운로드" @click="$download(summaryDetail.summaryImageDir)" />
            </div>
            <div>
                <KSASecondaryButton class="gray_button ml-xs" text="총괄표 복사" @click="copySummary" />
                <KSASecondaryButton class="gray_button ml-xs" text="총괄표 수정" @click="editSummary" />
                <KSASecondaryButton class="red_button ml-xs" text="총괄표 삭제" @click="deleteSummary" :disabled="!deleteYn" />
            </div>
        </div>
        <!-- s: 기초 과정 -->
        <div class="mt-xs">
            <div class="table-form curriculum-table">
                <table class="horizontal">
                    <colgroup>
                        <col width="50px">
                        <col width="110px">
                        <col />
                        <col width="100px">
                        <col width="80px">
                        <col width="80px">
                        <col width="180px">
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                    </colgroup>
                    <thead>
                        <tr>
                            <th rowspan="2">수준</th>
                            <th rowspan="2">분야</th>
                            <th rowspan="2">과정명</th>
                            <th rowspan="2">방식</th>
                            <th colspan="2">교육일수</th>
                            <th rowspan="2">대상</th>
                            <th colspan="12">연간교육일정</th>
                        </tr>
                        <tr>
                            <th>기간(일)</th>
                            <th>시간</th>
                            <th class="month">1월</th>
                            <th class="month">2월</th>
                            <th class="month">3월</th>
                            <th class="month">4월</th>
                            <th class="month">5월</th>
                            <th class="month">6월</th>
                            <th class="month">7월</th>
                            <th class="month">8월</th>
                            <th class="month">9월</th>
                            <th class="month">10월</th>
                            <th class="month">11월</th>
                            <th class="month">12월</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in curriculumList.filter((el) => el.curriculumGb === '01')" :key="item.curriculumId">
                            <td v-if="index === 0" :rowspan="curriculumList.filter((el) => el.curriculumGb === '01').length">기초</td>
                            <td @click="handleClickRow(item)">{{ item.curriculumFiled }}</td>
                            <td @click="handleClickRow(item)" class="text-left">
                                <span v-if="item.curriculumLabel" class="label" :class="{'blue_line': item.curriculumLabel === '채용연계', 'blue': item.curriculumLabel === '취업중점'}">{{ item.curriculumLabel }}</span>
                                {{ item.curriculumName }}
                            </td>
                            <td @click="handleClickRow(item)">{{ item.curriculumMth }}</td>
                            <td @click="handleClickRow(item)">{{ item.curriculumDays }}</td>
                            <td @click="handleClickRow(item)">{{ item.curriculumHours }}</td>
                            <td @click="handleClickRow(item)">{{ item.curriculumTrgt }}</td>
                            <td v-for="(month) in yearsArr" :key="year+index+month" @click="handleClickRow(item)">{{ item.annualSchedule.split(',').includes(month.toString()) ? 'O' : '' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <!-- e: 기초 과정 -->
        <!-- s: 심화 과정 -->
        <div class="mt-xs">
            <div class="table-form curriculum-table">
                <table class="horizontal">
                    <colgroup>
                        <col width="50px">
                        <col width="110px">
                        <col />
                        <col width="100px">
                        <col width="80px">
                        <col width="80px">
                        <col width="180px">
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                        <col width="40px"/>
                    </colgroup>
                    <thead>
                        <tr>
                            <th rowspan="2">수준</th>
                            <th rowspan="2">분야</th>
                            <th rowspan="2">과정명</th>
                            <th rowspan="2">방식</th>
                            <th colspan="2">교육일수</th>
                            <th rowspan="2">대상</th>
                            <th colspan="12">연간교육일정</th>
                        </tr>
                        <tr>
                            <th>기간(일)</th>
                            <th>시간</th>
                            <th class="month">1월</th>
                            <th class="month">2월</th>
                            <th class="month">3월</th>
                            <th class="month">4월</th>
                            <th class="month">5월</th>
                            <th class="month">6월</th>
                            <th class="month">7월</th>
                            <th class="month">8월</th>
                            <th class="month">9월</th>
                            <th class="month">10월</th>
                            <th class="month">11월</th>
                            <th class="month">12월</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in curriculumList.filter((el) => el.curriculumGb === '02')" :key="item.curriculumId">
                            <td v-if="index === 0" :rowspan="curriculumList.filter((el) => el.curriculumGb === '02').length">심화</td>
                            <td @click="handleClickRow(item)">{{ item.curriculumFiled }}</td>
                            <td @click="handleClickRow(item)" class="text-left">
                                <span v-if="item.curriculumLabel" class="label" :class="{'blue_line': item.curriculumLabel === '채용연계', 'blue': item.curriculumLabel === '취업중점'}">{{ item.curriculumLabel }}</span>
                                {{ item.curriculumName }}
                            </td>
                            <td @click="handleClickRow(item)">{{ item.curriculumMth }}</td>
                            <td @click="handleClickRow(item)">{{ item.curriculumDays }}</td>
                            <td @click="handleClickRow(item)">{{ item.curriculumHours }}</td>
                            <td @click="handleClickRow(item)">{{ item.curriculumTrgt }}</td>
                            <td v-for="(month) in yearsArr" :key="year+index+month" @click="handleClickRow(item)">{{ item.annualSchedule.split(',').includes(month.toString()) ? 'O' : '' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <!-- e: 심화 과정 -->
    </div>
    <SiteCurriculumDetailModal ref="curriculumModal" />
</template>

<style lang="scss" scoped>
@import "../../../../assets/scss/theme";

</style>
